import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Modal, Form, Input, Typography, message, Popconfirm, Tag, Row, Col, Empty, Badge, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TagsOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const ExpenseCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [form] = Form.useForm();

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.get('/api/accounting/categories', { headers });
            setCategories(res.data);
        } catch (error) {
            message.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const showModal = (category = null) => {
        setEditingCategory(category);
        if (category) {
            form.setFieldsValue(category);
        } else {
            form.resetFields();
            form.setFieldsValue({ color: '#1890ff' });
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingCategory(null);
    };

    const onFinish = async (values) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            if (editingCategory) {
                await axios.put(`/api/accounting/categories/${editingCategory.id}`, values, { headers });
                message.success('Category updated');
            } else {
                await axios.post('/api/accounting/categories', values, { headers });
                message.success('Category created');
            }
            setIsModalVisible(false);
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to save category');
        }
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            await axios.delete(`/api/accounting/categories/${id}`, { headers });
            message.success('Category deleted');
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to delete category');
        }
    };

    const filteredCategories = categories.filter(c =>
        c.name.toLowerCase().includes(searchText.toLowerCase()) ||
        (c.description && c.description.toLowerCase().includes(searchText.toLowerCase()))
    );

    const columns = [
        {
            title: 'Category Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text, record) => (
                <Space>
                    <Badge color={record.color} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Visual Tag',
            key: 'tag',
            render: (_, record) => (
                <Tag color={record.color} style={{ borderRadius: '4px', padding: '0 12px' }}>{record.name}</Tag>
            ),
            width: 180
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text) => <Text type="secondary">{text || 'No description provided'}</Text>,
            ellipsis: true
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => showModal(record)} />
                    <Popconfirm
                        title="Delete this category?"
                        description="This may fail if expenses are already linked."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Delete"
                        okType="danger"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>Expense Categories</Title>
                    <Text type="secondary">Define classifications for business spending</Text>
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
                    <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => showModal()}>
                        Create New Category
                    </Button>
                </Space>
            </div>

            <Card className="premium-card shadow-sm" bodyStyle={{ padding: 0 }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12} lg={8}>
                            <Input
                                placeholder="Search categories..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                allowClear
                                size="large"
                            />
                        </Col>
                    </Row>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredCategories}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    size="large"
                    locale={{
                        emptyText: searchText ? <Empty description="No categories match your search" /> :
                            <div style={{ padding: '40px 0' }}>
                                <Empty description="No categories defined yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                <Button type="primary" ghost icon={<PlusOutlined />} onClick={() => showModal()} style={{ marginTop: 16 }}>
                                    Add your first category
                                </Button>
                            </div>
                    }}
                />
            </Card>

            <Modal
                title={
                    <Space>
                        <TagsOutlined />
                        <span>{editingCategory ? 'Modify Category' : 'Create New Category'}</span>
                    </Space>
                }
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                destroyOnClose
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ color: '#1890ff' }}
                >
                    <Form.Item
                        name="name"
                        label="Category Name"
                        rules={[{ required: true, message: 'Unique name required' }]}
                    >
                        <Input placeholder="e.g., Office Supplies, Utilities, Rent" size="large" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description (Optional)"
                    >
                        <Input.TextArea placeholder="What type of expenses fall under this?" rows={4} />
                    </Form.Item>

                    <Form.Item
                        name="color"
                        label="Visual Accent Color"
                        rules={[{ required: true }]}
                    >
                        <Row gutter={10} wrap={true}>
                            {['#1890ff', '#52c41a', '#f5222d', '#faad14', '#722ed1', '#eb2f96', '#13c2c2', '#fa8c16', '#a0d911', '#2f54eb', '#fa541c'].map(c => (
                                <Col key={c}>
                                    <div
                                        onClick={() => form.setFieldsValue({ color: c })}
                                        style={{
                                            width: 36,
                                            height: 36,
                                            backgroundColor: c,
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            border: form.getFieldValue('color') === c ? '3px solid #000' : '1px solid rgba(0,0,0,0.1)',
                                            boxShadow: form.getFieldValue('color') === c ? '0 0 8px rgba(0,0,0,0.2)' : 'none',
                                            marginBottom: 10,
                                            transition: 'all 0.2s'
                                        }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Form.Item>

                    <Divider />

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space size="middle">
                            <Button onClick={handleCancel} size="large">Cancel</Button>
                            <Button type="primary" htmlType="submit" size="large">
                                {editingCategory ? 'Update Category' : 'Create Category'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ExpenseCategories;
